import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { CallProviderApiService } from '../services/call-provider-api.service';
import { PhoneNumberApiService } from '../services/phone-number-api.service';
import {
  CallProviderAccountRequest,
  CallProviderAccountResponse,
  NoAgentFallback
} from '../models/call-provider-account.model';
import { CALL_PROVIDER_LABELS, labelFrom } from '../call-labels';
import { PhoneNumberResponse } from '../models/phone-number.model';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';

@Component({
  selector: 'app-call-provider-config',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    HasPermissionDirective
  ],
  templateUrl: './call-provider-config.component.html'
})
export class CallProviderConfigComponent implements OnInit {
  loading = false;
  saving = false;
  verifying = false;
  error = '';
  showForm = false;
  config: CallProviderAccountResponse | null = null;
  phoneNumbers: PhoneNumberResponse[] = [];
  isSuperAdmin = false;

  readonly fallbackOptions: { value: NoAgentFallback; label: string }[] = [
    { value: 'HANGUP', label: 'Colgar (mensaje "agentes ocupados")' },
    { value: 'VOICEMAIL', label: 'Buzón de voz (grabar mensaje del caller)' }
  ];

  readonly accountSidRegex = /^AC[a-fA-F0-9]{32}$/;
  readonly apiKeySidRegex = /^SK[a-fA-F0-9]{32}$/;
  readonly twimlAppSidRegex = /^AP[a-fA-F0-9]{32}$/;
  readonly callbackUrlRegex = /^(https:\/\/[^\s]+|http:\/\/localhost(?::\d+)?(?:\/[^\s]*)?)$/;

  formAccountSid = '';
  formAuthToken = '';
  formApiKeySid = '';
  formApiKeySecret = '';
  formTwimlAppSid = '';
  formDefaultFromNumberId: number | null = null;
  formCallbackBaseUrl = '';
  formNoAgentFallback: NoAgentFallback = 'HANGUP';
  formIsActive = true;

  constructor(
    private readonly providerApi: CallProviderApiService,
    private readonly phoneNumberApi: PhoneNumberApiService,
    private readonly toast: ToastService,
    private readonly confirmDialog: ConfirmDialogService,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    this.isSuperAdmin = user?.roleName === 'Super Admin';
    if (!this.isSuperAdmin) return;
    this.loadAll();
  }

  loadAll(): void {
    this.loading = true;
    this.error = '';
    this.providerApi.getAccount().subscribe({
      next: (data) => {
        this.config = data;
        this.loading = false;
      },
      error: (err) => {
        if (err.status === 409 || err.status === 404) {
          this.config = null;
        } else {
          this.error = err.error?.message || 'Error al cargar la configuración Twilio';
        }
        this.loading = false;
      }
    });

    this.phoneNumberApi.listPhoneNumbers().subscribe({
      next: (nums) => { this.phoneNumbers = Array.isArray(nums) ? nums : []; },
      error: () => { this.phoneNumbers = []; }
    });
  }

  abrirFormulario(): void {
    this.error = '';
    if (this.config) {
      this.formAccountSid = this.config.accountSid;
      this.formAuthToken = '';
      this.formApiKeySid = '';
      this.formApiKeySecret = '';
      this.formTwimlAppSid = '';
      this.formDefaultFromNumberId = this.config.defaultFromNumberId ?? null;
      this.formCallbackBaseUrl = this.config.callbackBaseUrl ?? '';
      this.formNoAgentFallback = this.config.noAgentFallback ?? 'HANGUP';
      this.formIsActive = this.config.isActive ?? true;
    } else {
      this.resetForm();
    }
    this.showForm = true;
  }

  cancelar(): void {
    this.showForm = false;
    this.error = '';
  }

  private resetForm(): void {
    this.formAccountSid = '';
    this.formAuthToken = '';
    this.formApiKeySid = '';
    this.formApiKeySecret = '';
    this.formTwimlAppSid = '';
    this.formDefaultFromNumberId = null;
    this.formCallbackBaseUrl = '';
    this.formNoAgentFallback = 'HANGUP';
    this.formIsActive = true;
  }

  guardar(): void {
    const accountSid = this.formAccountSid.trim();
    if (!this.accountSidRegex.test(accountSid)) {
      this.error = 'El Account SID debe tener el formato AC + 32 caracteres hexadecimales';
      return;
    }

    const isCreate = !this.config;
    const authToken = this.formAuthToken.trim();
    if (isCreate && !authToken) {
      this.error = 'El Auth Token es requerido en la creación inicial';
      return;
    }
    if (authToken && (authToken.length < 32 || authToken.length > 64)) {
      this.error = 'El Auth Token debe tener entre 32 y 64 caracteres';
      return;
    }

    const apiKeySid = this.formApiKeySid.trim();
    if (apiKeySid && !this.apiKeySidRegex.test(apiKeySid)) {
      this.error = 'El API Key SID debe tener el formato SK + 32 caracteres hexadecimales';
      return;
    }

    const apiKeySecret = this.formApiKeySecret.trim();
    if (apiKeySecret && (apiKeySecret.length < 32 || apiKeySecret.length > 64)) {
      this.error = 'El API Key Secret debe tener entre 32 y 64 caracteres';
      return;
    }

    const twimlAppSid = this.formTwimlAppSid.trim();
    if (twimlAppSid && !this.twimlAppSidRegex.test(twimlAppSid)) {
      this.error = 'El TwiML App SID debe tener el formato AP + 32 caracteres hexadecimales';
      return;
    }

    const callbackUrl = this.formCallbackBaseUrl.trim();
    if (callbackUrl && !this.callbackUrlRegex.test(callbackUrl)) {
      this.error = 'El Callback Base URL debe ser HTTPS (o http://localhost en desarrollo)';
      return;
    }

    const request: CallProviderAccountRequest = {
      accountSid,
      noAgentFallback: this.formNoAgentFallback,
      isActive: this.formIsActive
    };

    if (authToken) request.authToken = authToken;
    if (apiKeySid) request.apiKeySid = apiKeySid;
    if (apiKeySecret) request.apiKeySecret = apiKeySecret;
    if (twimlAppSid) request.twimlAppSid = twimlAppSid;
    if (callbackUrl) request.callbackBaseUrl = callbackUrl;
    if (this.formDefaultFromNumberId != null) request.defaultFromNumberId = this.formDefaultFromNumberId;

    this.saving = true;
    this.error = '';

    this.providerApi.upsertAccount(request).subscribe({
      next: (data) => {
        this.config = data;
        this.saving = false;
        this.showForm = false;
        if (isCreate) {
          this.toast.created('Configuración Twilio');
        } else {
          this.toast.updated('Configuración Twilio');
        }
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar la configuración Twilio';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  verificar(): void {
    if (!this.config) return;
    this.verifying = true;
    this.error = '';
    this.providerApi.verifyAccount().subscribe({
      next: (res) => {
        this.verifying = false;
        if (res.valid) {
          if (this.config) this.config.lastVerifiedAt = res.verifiedAt ?? new Date().toISOString();
          this.toast.success({ title: 'Twilio verificado', message: 'Las credenciales son válidas.' });
        } else {
          this.toast.error({ title: 'Credenciales inválidas', message: 'Twilio rechazó accountSid + authToken. Revísalos en la consola Twilio.' });
        }
      },
      error: (err) => {
        this.verifying = false;
        const msg = err.error?.message || 'Error al verificar las credenciales Twilio';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  async eliminar(): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar configuración Twilio',
      message: '¿Eliminar definitivamente la configuración? Las llamadas dejarán de funcionar hasta que se reconfigure.'
    });
    if (!ok) return;

    this.providerApi.deleteAccount().subscribe({
      next: () => {
        this.config = null;
        this.toast.deleted('Configuración Twilio');
      },
      error: (err) => {
        const msg = err.error?.message || 'Error al eliminar la configuración Twilio';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  defaultNumberLabel(): string {
    const id = this.config?.defaultFromNumberId;
    if (id == null) return 'Sin asignar';
    const found = this.phoneNumbers.find(n => n.id === id);
    return found ? `${found.phoneNumber}${found.friendlyName ? ' — ' + found.friendlyName : ''}` : `#${id}`;
  }

  providerLabel(value: string | null | undefined): string {
    return labelFrom(CALL_PROVIDER_LABELS, value);
  }

  fallbackLabel(value: NoAgentFallback | undefined): string {
    return this.fallbackOptions.find(o => o.value === value)?.label ?? value ?? '—';
  }
}
